import { DisplayObject, type RenderState } from "./display";
import { Rectangle } from "./geom";

export class TextFormat {
  constructor(
    public font: string | null = null,
    public size: number | null = null,
    public color: number | null = null,
    public bold: boolean | null = null,
    public italic: boolean | null = null,
    public underline: boolean | null = null,
    public url: string | null = null,
    public target: string | null = null,
    public align: string | null = null,
    public leftMargin: number | null = null,
    public rightMargin: number | null = null,
    public indent: number | null = null,
    public leading: number | null = null,
  ) {}

  clone(): TextFormat {
    return new TextFormat(
      this.font,
      this.size,
      this.color,
      this.bold,
      this.italic,
      this.underline,
      this.url,
      this.target,
      this.align,
      this.leftMargin,
      this.rightMargin,
      this.indent,
      this.leading,
    );
  }
}

export class TextField extends DisplayObject {
  // Inert in the port: the fonts are already registered through CSS and no
  // field is interactive, but the ported code sets both on every TextField.
  embedFonts = false;
  selectable = true;
  wordWrap = false;

  get defaultTextFormat(): TextFormat {
    return this.format.clone();
  }

  set defaultTextFormat(format: TextFormat) {
    this.format = format.clone();
    this.invalidate();
  }

  get text(): string {
    return this.value;
  }

  set text(v: string) {
    this.value = v == null ? "" : String(v);
    this.lines = null;
  }

  // TextField width/height size the box, they do not scale like other
  // display objects, so the DisplayObject scale-based accessors are replaced.
  override get width(): number {
    return this.boxWidth * this.scaleX;
  }

  override set width(v: number) {
    this.boxWidth = v;
    this.bounds.width = v;
    this.lines = null;
  }

  override get height(): number {
    return this.boxHeight * this.scaleY;
  }

  override set height(v: number) {
    this.boxHeight = v;
    this.bounds.height = v;
    this.lines = null;
  }

  $localBounds(): Rectangle | null {
    return this.bounds;
  }

  protected $draw(state: RenderState): void {
    const lines = this.layout();
    if (lines.length === 0) return;
    const ctx = state.ctx;

    let color = this.format.color ?? 0;
    const ct = state.colorTransform;
    if (ct && !ct.isIdentity) {
      const r = Math.min(
        255,
        Math.max(0, ((color >> 16) & 0xff) * ct.redMultiplier + ct.redOffset),
      );
      const g = Math.min(
        255,
        Math.max(
          0,
          ((color >> 8) & 0xff) * ct.greenMultiplier + ct.greenOffset,
        ),
      );
      const b = Math.min(
        255,
        Math.max(0, (color & 0xff) * ct.blueMultiplier + ct.blueOffset),
      );
      color = (r << 16) | (g << 8) | b;
    }
    if (color !== this.colorValue) {
      this.colorValue = color;
      this.colorCss = `#${(color & 0xffffff).toString(16).padStart(6, "0")}`;
    }
    ctx.globalAlpha = state.alpha * (ct ? ct.alphaMultiplier : 1);
    ctx.fillStyle = this.colorCss;
    ctx.font = this.cssFont();
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const size = this.format.size ?? 12;
    const lineHeight = this.lineHeight();
    const ascent = Math.ceil(size * 0.94);
    const align = this.format.align ?? "left";
    const inner = this.boxWidth - GUTTER * 2;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      let x = GUTTER;
      if (align === "right") x = GUTTER + inner - line.width;
      else if (align === "center") x = GUTTER + (inner - line.width) / 2;
      ctx.fillText(line.text, x, GUTTER + ascent + i * lineHeight);
    }
  }

  private format = new TextFormat();
  private value = "";
  private boxWidth = 100;
  private boxHeight = 100;
  private lines: Line[] | null = null;
  private readonly bounds = new Rectangle(0, 0, 100, 100);
  private font = "";
  private colorValue = -1;
  private colorCss = "#000000";

  private invalidate(): void {
    this.lines = null;
    this.font = "";
  }

  /** Memoised: the format only changes when a new one is assigned. */
  private cssFont(): string {
    if (this.font) return this.font;
    const size = this.format.size ?? 12;
    const weight = this.format.bold ? "bold" : "normal";
    const style = this.format.italic ? "italic" : "normal";
    const family = this.format.font ? `"${this.format.font}"` : "sans-serif";
    this.font = `${style} ${weight} ${size}px ${family}`;
    return this.font;
  }

  private lineHeight(): number {
    const size = this.format.size ?? 12;
    // Flash stacks lines by the font's own ascent+descent plus leading.
    return Math.ceil(size * 1.2) + (this.format.leading ?? 0);
  }

  private layout(): Line[] {
    if (this.lines) return this.lines;
    const ctx = measureContext();
    ctx.font = this.cssFont();
    const limit = this.boxWidth - GUTTER * 2;
    const out: Line[] = [];
    for (const paragraph of this.value.split(/\r\n|\r|\n/)) {
      const paragraphWidth = ctx.measureText(paragraph).width;
      if (!this.wordWrap || paragraphWidth <= limit) {
        out.push({ text: paragraph, width: paragraphWidth });
        continue;
      }
      let current = "";
      let currentWidth = 0;
      for (const word of paragraph.split(" ")) {
        const candidate = current ? `${current} ${word}` : word;
        const candidateWidth = ctx.measureText(candidate).width;
        if (candidateWidth <= limit || current === "") {
          current = candidate;
          currentWidth = candidateWidth;
        } else {
          out.push({ text: current, width: currentWidth });
          current = word;
          currentWidth = ctx.measureText(word).width;
        }
      }
      out.push({ text: current, width: currentWidth });
    }
    this.lines = out;
    return out;
  }
}

/** Flash reserves a 2px gutter inside a TextField's box on every side. */
const GUTTER = 2;

interface Line {
  text: string;
  width: number;
}

let measureCtx: CanvasRenderingContext2D | null = null;

function measureContext(): CanvasRenderingContext2D {
  if (measureCtx) return measureCtx;
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = 8;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  measureCtx = ctx;
  return ctx;
}
